// react and mui stuff
import { useContext, useEffect, useState } from "react";
import { ClickAwayListener } from "@mui/base";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../utils/contexts";
import { ToastContext } from "../components/toast";

import AspectRatio from "@mui/joy/AspectRatio"
import Avatar from "@mui/joy/Avatar"
import Button from "@mui/joy/Button"
import Card from "@mui/joy/Card"
import CardActions from "@mui/joy/CardActions"
import CardContent from "@mui/joy/CardContent"
import CardOverflow from "@mui/joy/CardOverflow"
import FormControl from "@mui/joy/FormControl"
import FormLabel from "@mui/joy/FormLabel"
import IconButton from "@mui/joy/IconButton"
import Option from "@mui/joy/Option"
import Select from "@mui/joy/Select"
import Skeleton from "@mui/joy/Skeleton"
import Tooltip from "@mui/joy/Tooltip"
import Typography from "@mui/joy/Typography"

import FirstPage from "@mui/icons-material/FirstPage"
import LastPage from "@mui/icons-material/LastPage"
import NavigateBefore from "@mui/icons-material/NavigateBefore"
import NavigateNext from "@mui/icons-material/NavigateNext"
import KeyboardVoice from "@mui/icons-material/KeyboardVoice"
import Textsms from "@mui/icons-material/Textsms"

// custom components
import { MetaProps } from "../components/metaprops";

// data and stuff
import { BaseUser, CliUser } from "../types/users";
import { PHBaseUsers } from "../data/placeholderusers";
import { setData } from "../utils/setdata";
import { auditAPI, userAPI } from "../utils/backend";

const perPageOptions = [
  10,
  20,
  50,
  100,
  200,
  500
];

export default function PFPAudit() {
  const [page, setPage] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(perPageOptions[1]);
  const [displaying, setDisplaying] = useState<BaseUser[]>(PHBaseUsers(perPage));

  const [fullData, setFullData] = useState<BaseUser[]>(PHBaseUsers(perPage));
  const [selected, setSelected] = useState<string>();
  const [loading, setLoading] = useState(false);

  const { setToast } = useContext(ToastContext);
  const { user, setUser } = useContext(UserContext);
  const nav = useNavigate();

  // data fetching effect
  useEffect(() => {
    // get data
    if (!user) return;
    auditAPI.getAvatars().then(info => {
      if (!setData(info, setToast, user, setUser)) return;

      setPage(0);
      setFullData(info);
    })
  }, [setToast, setUser, user]);

  useEffect(() => {
    // when stuff is loaded
    if (fullData) {
      // get the last page number
      const maxPage = Math.ceil(fullData.length / perPage);

      // ensure boundaries are kept
      if (page > maxPage) setPage(maxPage - 1);
      if (page < 0) setPage(0);

      // show the data
      setDisplaying(fullData.slice(page * perPage, ((page + 1) * perPage)));
    }
  }, [page, fullData, perPage]);

  function changePerPage(e: any, num: number | null) {
    setSelected(undefined);
    setPage(0);
    setPerPage(num ?? perPageOptions[1]);
  }

  function changePage(num: number) {
    setSelected(undefined);
    setPage(num);
  }

  function confirmKick(user: BaseUser, cliUser: CliUser | null | undefined) {
    const confirmed = window.confirm(`Are you sure you want to kick ${user.displayName}?`);
    if (!confirmed) return;

    setLoading(true)
    const reason = "Innapropriate profile picture.";

    userAPI.kick(user.id, reason).then(status => {
      const onFinish = () => {
        setLoading(false)
        setSelected(undefined);
      }
      if (!setData(status, setToast, cliUser, setUser, onFinish)) return;

      // get new info
      auditAPI.getAvatars().then(info => {
        
        if (!setData(info, setToast, cliUser, setUser, onFinish)) return;

        setToast({ text: `${user.displayName} has been kicked!`, color: "success" });
        setFullData(info);
        onFinish();
      });
    })
  }

  // same last page calculation as before
  const maxPage = Math.ceil(fullData.length / perPage) - 1
  return (
    <div className="contentRoot">
      <MetaProps meta={{
        splash: "april fools",
        title: "PFP Audit",
        url: "mod/pfps",
        description: "Get a list of profile pictures in the server"
      }}
      />
      <h1 className="pageTitle">PFP Audit</h1>

      {/** Per Page Selector */}
      <div className="flexMidVert" style={{justifyContent: "flex-end"}}>
        <FormControl>
          <FormLabel>Avatars Per Page</FormLabel>
          <Select value={perPage} onChange={changePerPage} sx={{width: "100px"}} name="perPage">
            {perPageOptions.map(o => <Option value={o} key={o}>{o}</Option>)}
          </Select>
        </FormControl>
      </div>

      <br/>
      {/** Avatars */}
      <div className="flexMidVert" style={{ flexWrap: "wrap", justifyContent: "center" }}>
        {
          displaying.map(usr => {
            if (!usr.avatar) return <Avatar size="lg" sx={{ m: "5px" }} alt="?" key={usr.id}><Skeleton/></Avatar>
            return (
              <Tooltip component="div"
                open={selected === usr.id}
                disableHoverListener disableFocusListener disableTouchListener arrow
                onClose={() => setSelected(undefined)}
                key={usr.id}
                title={
                  // user popup card
                  <ClickAwayListener onClickAway={() => setSelected(undefined)}>

                  <Card sx={{ width: 300, textAlign: "center", alignItems: "center", borderColor: "black" }}>
                    {/** Popup Header */}
                    <CardOverflow sx={{padding: "0px"}}>
                      <div style={{height: "100px", backgroundColor: usr.color, width: "100%", borderRadius: "6px 6px 0px 0px"}}/>
                      <AspectRatio ratio="1" sx={{ width: "100px", m: "auto", position: "relative", transform: "translateY(-50%)", borderRadius: "50%", boxShadow: "sm" }}>
                        <Avatar src={usr.avatar + "?size=100"} alt={usr.displayName}/>
                      </AspectRatio>
                    </CardOverflow>

                    {/** Popup Content */}
                    <CardContent sx={{width: "100%"}}>
                      <Typography className="noTextOverflow" level="title-lg" sx={{ mt: "-50px" }}>
                        {usr.displayName}
                      </Typography>
                      {usr.infractionCount !== undefined && <Typography className="noTextOverflow">Infractions: {usr.infractionCount}</Typography>}
                      <div className="flexMidHoriz flexMidVert" style={{ justifyContent: "space-evenly" }}>
                        <Typography startDecorator={<Textsms/>}>{usr.activePosts}</Typography>
                        <Typography startDecorator={<KeyboardVoice/>}>{usr.activeVoice}</Typography>
                      </div>

                      <CardActions>
                        <Button fullWidth onClick={() => nav(`/profile/${usr.id}`)} disabled={loading}>View Profile</Button>
                        <Button fullWidth onClick={() => confirmKick(usr, user)} color="danger" loading={loading}>Kick</Button>
                      </CardActions>

                    </CardContent>
                  </Card>
                </ClickAwayListener>
                }
              >
                <Avatar alt={usr.displayName} src={usr.avatar + "?size=48"} size="lg" sx={{ m: "5px", cursor: "pointer" }} slotProps={{ img: { loading: "lazy" } }} onClick={() => setSelected(usr.id)}/>
              </Tooltip>
            )
          })
        }
      </div>
      
      <br/>
      {/** Page Navigation */}
      <div className="flexMidVert" style={{ justifyContent: "center" }}>
        <IconButton aria-label="First Page" disabled={page === 0} onClick={() => changePage(0)}><FirstPage/></IconButton>
        <IconButton aria-label="Previous Page" disabled={page === 0} onClick={() => changePage(page - 1)}><NavigateBefore/></IconButton>

        <Typography fontWeight="bold" sx={{ m: "0px 5px"}}>{page + 1}</Typography>

        <IconButton aria-label="Next Page" disabled={page === maxPage} onClick={() => changePage(page + 1)}><NavigateNext/></IconButton>
        <IconButton aria-label="Last Page" disabled={page === maxPage} onClick={() => changePage(maxPage)}><LastPage/></IconButton>
      </div>
    </div>
  )
}